import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ScannerSection, type ScannerSectionHandle } from '../scanner';
import { QR_NOT_FOUND_MESSAGE } from '../scanner/qrScanMessages';
import {
  checkInLabel,
  checkOutLabel,
  getCheckedOutStorageIdPrefixes,
  getLabelByStorageIdPrefix,
  getLabelStatus,
  getStorageIdPrefixes,
} from '../../services/shelfIndexAPI';
import { useClickOutside } from '../../hooks/useClickOutside';
import {
  MESSAGE_TONES,
  cameraMessageClass,
  statusSlotMessageClass,
  useStatusMessage,
} from '../../hooks/useStatusMessage';
import ScanQueueChips, { type ScanQueueChip } from '../shared/ScanQueueChips';
import OrderDetailCameraBar, { CameraIcon } from '../orders/orderdetail/OrderDetailCameraBar';
import { extractStorageIdFromQr } from '../orders/orderdetail/scanningLogic';
import { SCAN_LABEL_PREFIX_QUERY, parseStorageIdPrefixParam } from '../../utils/scanLabelHandoff';
import './PrintBoxLabel.css';
import '../orders/orderdetail/pick-list.css';

const NOT_FOUND_MESSAGE = 'ID not found in database.';
const ALL_CHECKED_IN_MESSAGE = 'All labels need to be checked in';
const ALL_CHECKED_OUT_MESSAGE = 'All labels need to be checked out';
const SUCCESS_MESSAGE_MS = 5000;

const REGISTER_STATUS = {
  CHECKED_IN: 'checked in',
  CHECKED_OUT: 'checked out',
} as const;

type RegisterStatusLabel = typeof REGISTER_STATUS[keyof typeof REGISTER_STATUS];

interface LabelRegisterChip extends ScanQueueChip {
  readonly statusLabel: RegisterStatusLabel;
  readonly checkedIn: boolean;
}

function CheckLabelOut(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const prefixHandoffAppliedRef = useRef(false);
  const scannerCaptureRef = useRef<ScannerSectionHandle>(null);
  const queueRef = useRef<LabelRegisterChip[]>([]);
  const submittingRef = useRef(false);
  const [scanQueue, setScanQueue] = useState<LabelRegisterChip[]>([]);
  const [webcamOn, setWebcamOn] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualStorageId, setManualStorageId] = useState('');
  const [manualStorageIdDebounced, setManualStorageIdDebounced] = useState('');
  const [checkedInPrefixes, setCheckedInPrefixes] = useState<string[]>([]);
  const [checkedOutPrefixes, setCheckedOutPrefixes] = useState<string[]>([]);
  const [filteredPrefixes, setFilteredPrefixes] = useState<string[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const skipAutocompleteOpenRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    message,
    tone: messageTone,
    invalidInput: errorInvalidInput,
    flashKey: messageKey,
    fades: messageFades,
    showMessage,
    clearMessage,
  } = useStatusMessage();

  const replaceQueue = useCallback((next: LabelRegisterChip[]): void => {
    queueRef.current = next;
    setScanQueue(next);
  }, []);

  const allPrefixes = useMemo((): string[] => {
    const seen = new Set<string>();
    const merged: string[] = [];
    for (const prefix of [...checkedInPrefixes, ...checkedOutPrefixes]) {
      const normalized = prefix.toLowerCase();
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      merged.push(normalized);
    }
    return merged;
  }, [checkedInPrefixes, checkedOutPrefixes]);

  const addChainRef = useRef(Promise.resolve());

  const addLabel = useCallback(async (rawPrefix: string): Promise<boolean> => {
    if (submittingRef.current) return false;
    const normalized = rawPrefix.trim().toLowerCase();
    if (normalized.length !== 6) {
      const badValue = normalized || '(empty)';
      showMessage(`Storage ID ${badValue} must be 6 characters`, MESSAGE_TONES.ERROR, {
        invalidInput: badValue,
      });
      return false;
    }
    if (queueRef.current.some((item) => item.prefix === normalized)) {
      showMessage(`${normalized} already scanned.`, MESSAGE_TONES.ERROR);
      return false;
    }

    const [status, label] = await Promise.all([
      getLabelStatus(normalized),
      getLabelByStorageIdPrefix(normalized),
    ]);
    if (submittingRef.current) return false;

    const sku = label?.sku?.trim() ?? '';
    if (!status.found || !sku) {
      showMessage(NOT_FOUND_MESSAGE, MESSAGE_TONES.ERROR, { invalidInput: normalized });
      return false;
    }
    if (queueRef.current.some((item) => item.prefix === normalized)) {
      showMessage(`${normalized} already scanned.`, MESSAGE_TONES.ERROR);
      return false;
    }

    const first = queueRef.current[0];
    if (first && first.checkedIn !== status.checkedIn) {
      showMessage(
        first.checkedIn ? ALL_CHECKED_IN_MESSAGE : ALL_CHECKED_OUT_MESSAGE,
        MESSAGE_TONES.ERROR,
      );
      return false;
    }

    const chip: LabelRegisterChip = {
      prefix: normalized,
      sku,
      checkedIn: status.checkedIn,
      statusLabel: status.checkedIn ? REGISTER_STATUS.CHECKED_IN : REGISTER_STATUS.CHECKED_OUT,
    };
    replaceQueue([...queueRef.current, chip]);
    showMessage(`✓ Added ${normalized}`, MESSAGE_TONES.SUCCESS, { autoClearMs: SUCCESS_MESSAGE_MS });
    return true;
  }, [replaceQueue, showMessage]);

  const enqueueAdd = useCallback((rawPrefix: string): void => {
    addChainRef.current = addChainRef.current
      .then(() => addLabel(rawPrefix))
      .then(() => undefined, () => undefined);
  }, [addLabel]);

  useEffect(() => {
    if (prefixHandoffAppliedRef.current) return;
    const prefix = parseStorageIdPrefixParam(searchParams.get(SCAN_LABEL_PREFIX_QUERY));
    if (!prefix) return;
    prefixHandoffAppliedRef.current = true;
    enqueueAdd(prefix);
  }, [searchParams, enqueueAdd]);

  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const [checkedIn, checkedOut] = await Promise.all([
          getStorageIdPrefixes(),
          getCheckedOutStorageIdPrefixes(),
        ]);
        if (cancelled) return;
        setCheckedInPrefixes(checkedIn);
        setCheckedOutPrefixes(checkedOut);
      } catch (error: unknown) {
        console.error('Error loading storage ID prefixes:', error);
        if (!cancelled) {
          showMessage('Error loading storage IDs. Please refresh the page.', MESSAGE_TONES.ERROR);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [showMessage]);

  useEffect(() => {
    if (skipAutocompleteOpenRef.current) {
      skipAutocompleteOpenRef.current = false;
      if (manualStorageIdDebounced.length > 0) {
        const searchTerm = manualStorageIdDebounced.toLowerCase().trim();
        setFilteredPrefixes(
          allPrefixes.filter((prefix) => prefix.includes(searchTerm)).slice(0, 10),
        );
      }
      setSelectedIndex(-1);
      setShowAutocomplete(false);
      return;
    }
    const term = manualStorageIdDebounced.toLowerCase().trim();
    if (term.length === 0) {
      setFilteredPrefixes([]);
      setShowAutocomplete(false);
      setSelectedIndex(-1);
      return;
    }
    const id = setTimeout(() => {
      const filtered = allPrefixes.filter((prefix) => prefix.includes(term)).slice(0, 10);
      setFilteredPrefixes(filtered);
      setShowAutocomplete(filtered.length > 0);
      setSelectedIndex(-1);
    }, 0);
    return () => clearTimeout(id);
  }, [manualStorageIdDebounced, allPrefixes]);

  useEffect(() => {
    if (showAutocomplete && inputRef.current && autocompleteRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const dropdown = autocompleteRef.current;
      dropdown.style.top = `${inputRect.bottom}px`;
      dropdown.style.left = `${inputRect.left}px`;
      dropdown.style.width = `${inputRect.width}px`;
    }
  }, [showAutocomplete, manualStorageIdDebounced, filteredPrefixes]);

  const handleCapture = useCallback((qrData: string | null): Promise<boolean> => {
    const prefix = extractStorageIdFromQr(qrData);
    if (!prefix) {
      showMessage(QR_NOT_FOUND_MESSAGE, MESSAGE_TONES.ERROR, { fade: true });
      return Promise.resolve(false);
    }
    const pending = addChainRef.current
      .then(() => addLabel(prefix))
      .then((added) => added, () => false);
    addChainRef.current = pending.then(() => undefined, () => undefined);
    return pending;
  }, [addLabel, showMessage]);

  const openManualInput = (): void => {
    setShowManualInput(true);
    setManualStorageId('');
    setManualStorageIdDebounced('');
    setTimeout(() => {
      const modal = document.querySelector('.pbl-modal-overlay');
      if (modal) modal.scrollIntoView({ behavior: 'smooth', block: 'start' });
      inputRef.current?.focus();
    }, 100);
  };

  const handleManualInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value.toLowerCase().substring(0, 6);
    setManualStorageId(value);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setManualStorageIdDebounced(value.trim());
    }, 500);
  };

  const handleManualInputClose = (): void => {
    setShowManualInput(false);
    setManualStorageId('');
    setManualStorageIdDebounced('');
    setShowAutocomplete(false);
  };

  const selectPrefix = (prefix: string): void => {
    skipAutocompleteOpenRef.current = true;
    setManualStorageIdDebounced(prefix);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
    setShowManualInput(false);
    setManualStorageId('');
    enqueueAdd(prefix);
  };

  const handleManualInputSubmit = (): void => {
    const value = manualStorageId.trim().toLowerCase();
    setShowManualInput(false);
    setManualStorageId('');
    setManualStorageIdDebounced('');
    if (!value) return;
    enqueueAdd(value);
  };

  const handleManualInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!showAutocomplete || filteredPrefixes.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev < filteredPrefixes.length - 1 ? prev + 1 : prev));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (event.key === 'Enter' && selectedIndex >= 0) {
      event.preventDefault();
      selectPrefix(filteredPrefixes[selectedIndex]);
    } else if (event.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  useClickOutside(
    [autocompleteRef, inputRef],
    () => setShowAutocomplete(false),
  );

  const removeChip = (prefix: string): void => {
    replaceQueue(queueRef.current.filter((item) => item.prefix !== prefix));
  };

  const handleSubmit = async (): Promise<void> => {
    const queued = queueRef.current;
    if (queued.length === 0 || submittingRef.current) return;
    const checkingOut = queued[0].checkedIn;
    submittingRef.current = true;
    setIsSubmitting(true);
    showMessage(checkingOut ? 'Checking out...' : 'Checking in...');
    const action = checkingOut ? checkOutLabel : checkInLabel;
    const failed: LabelRegisterChip[] = [];
    const succeeded = new Set<string>();
    let errorMessage = '';
    try {
      for (const chip of queued) {
        const result = await action(chip.prefix);
        if (result.success) {
          succeeded.add(chip.prefix);
        } else {
          failed.push(chip);
          errorMessage = result.message || (checkingOut ? 'Failed to check out label.' : 'Failed to check in label.');
        }
      }
    } catch (error: unknown) {
      console.error('Error submitting label register batch:', error);
      errorMessage = checkingOut ? 'Error checking out. Please try again.' : 'Error checking in. Please try again.';
      for (const chip of queued) {
        if (!succeeded.has(chip.prefix) && !failed.some((item) => item.prefix === chip.prefix)) {
          failed.push(chip);
        }
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }

    if (checkingOut) {
      setCheckedInPrefixes((prev) => prev.filter((prefix) => !succeeded.has(prefix.toLowerCase())));
      setCheckedOutPrefixes((prev) => {
        const next = new Set(prev.map((prefix) => prefix.toLowerCase()));
        for (const prefix of succeeded) next.add(prefix);
        return [...next];
      });
    } else {
      setCheckedOutPrefixes((prev) => prev.filter((prefix) => !succeeded.has(prefix.toLowerCase())));
      setCheckedInPrefixes((prev) => {
        const next = new Set(prev.map((prefix) => prefix.toLowerCase()));
        for (const prefix of succeeded) next.add(prefix);
        return [...next];
      });
    }

    if (failed.length === 0) {
      replaceQueue([]);
      clearMessage();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 1000);
      return;
    }
    replaceQueue(failed);
    showMessage(errorMessage, MESSAGE_TONES.ERROR);
  };

  const batchCheckedIn = scanQueue[0]?.checkedIn;
  const multipleLabels = scanQueue.length > 1;
  const submitLabel = isSubmitting
    ? (batchCheckedIn === false ? 'Checking In...' : 'Checking Out...')
    : batchCheckedIn === false
      ? (multipleLabels ? 'Check In Labels' : 'Check Label In')
      : (multipleLabels ? 'Check Out Labels' : 'Check Out Label');

  const isSuccessMessage = messageTone === MESSAGE_TONES.SUCCESS;
  const messageBody = errorInvalidInput && message.includes(errorInvalidInput) ? (
    <>
      {message.split(errorInvalidInput)[0]}
      <span className="pbl-message-invalid">{errorInvalidInput}</span>
      {message.split(errorInvalidInput).slice(1).join(errorInvalidInput)}
    </>
  ) : (
    message
  );
  const cameraMessageOverlay = message ? (
    <div
      key={isSuccessMessage ? `success-${messageKey}` : 'status'}
      className={cameraMessageClass(messageTone, messageFades)}
      role="status"
    >
      {messageBody}
    </div>
  ) : null;

  return (
    <div className={`pbl-container pbl-shelf-indexer${webcamOn ? ' pbl-camera-open' : ''}`}>
      {showSuccess && (
        <div className="success-flash">
          <div className="success-message">✓ Success!</div>
        </div>
      )}

      <header className="pbl-page-header">
        <div className="pbl-page-header-status" role="status" aria-live="polite">
          {message && !webcamOn ? (
            <div
              key={isSuccessMessage ? `success-${messageKey}` : 'status'}
              className={statusSlotMessageClass(messageTone)}
            >
              {messageBody}
            </div>
          ) : null}
        </div>
      </header>

      <div className="pbl-page-body">
        <div className="pbl-section">
          <div className="pbl-section-title pbl-section-info">
            <span className="pbl-section-text">Storage ID</span>
          </div>
          <div className="pbl-info-display">
            <div className="pbl-info-row">
              <span className="pbl-info-label">Storage ID:</span>
              <div className="pbl-info-value-wrapper">
                <span
                  className="pbl-info-value pbl-info-clickable pbl-info-empty"
                  onClick={openManualInput}
                >
                  Enter
                </span>
              </div>
            </div>
          </div>
          <div className="pbl-scan-repo">
            <div className="pbl-scan-repo-notice">Scan labels</div>
            <ScanQueueChips
              layout="stack"
              items={scanQueue}
              onRemove={removeChip}
            />
          </div>
        </div>

        <div className="pbl-submit-section">
          <button
            type="button"
            onClick={() => { void handleSubmit(); }}
            disabled={scanQueue.length === 0 || isSubmitting}
            className="pbl-submit-button pbl-submit-button-main"
          >
            {submitLabel}
          </button>
        </div>
      </div>

      {showManualInput && (
        <div className="pbl-modal-overlay" onClick={handleManualInputClose}>
          <div className="pbl-modal" onClick={(event) => event.stopPropagation()}>
            <button className="pbl-modal-close" onClick={handleManualInputClose}>
              ×
            </button>
            <div className="pbl-modal-title">Enter Storage ID</div>
            <div className="pbl-manual-popup">
              <div className="pbl-sku-input-wrapper">
                <input
                  ref={inputRef}
                  type="text"
                  value={manualStorageId}
                  maxLength={6}
                  onChange={handleManualInputChange}
                  onKeyDown={handleManualInputKeyDown}
                  placeholder="e.g. a1b2c3"
                  className="pbl-sku-input"
                  onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Enter' && selectedIndex < 0) {
                      handleManualInputSubmit();
                    }
                  }}
                />
                {showAutocomplete && filteredPrefixes.length > 0 && (
                  <div ref={autocompleteRef} className="pbl-autocomplete-dropdown">
                    {filteredPrefixes.map((prefix, index) => (
                      <div
                        key={prefix}
                        className={`pbl-autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
                        onClick={() => selectPrefix(prefix)}
                        onMouseEnter={() => setSelectedIndex(index)}
                      >
                        {prefix}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleManualInputSubmit}
                className="pbl-submit-button"
              >
                Set Storage ID
              </button>
            </div>
          </div>
        </div>
      )}

      <OrderDetailCameraBar
        open={webcamOn}
        onShutter={() => scannerCaptureRef.current?.capture()}
        centerActions={
          <button
            type="button"
            className="order-detail-bar-camera-btn order-detail-bar-camera-btn-center"
            onClick={() => setWebcamOn(true)}
            aria-label="Open camera"
          >
            <CameraIcon size={28} />
          </button>
        }
      >
        <ScannerSection
          ref={scannerCaptureRef}
          mode="qr"
          onCapture={handleCapture}
          webcamOn={webcamOn}
          setWebcamOn={setWebcamOn}
          className="webcam-capture-container pbl-shelf-indexer-scanner"
          overlay={cameraMessageOverlay}
          cornerAction={
            <button
              type="button"
              className="webcam-viewport-close-btn"
              onClick={(event) => {
                event.stopPropagation();
                setWebcamOn(false);
              }}
              aria-label="Close camera"
            >
              ×
            </button>
          }
        />
      </OrderDetailCameraBar>
    </div>
  );
}

export default CheckLabelOut;
